import deepClone from "lodash.clonedeep";
import { stickyModule } from "./stickyModule";
import { marker } from "./marker";
import { onElementBoundsOut, getState } from "./onElementOut";
import { MainSearchUnAuth } from "../../components/search-field/mainSearch";
import PubSub from "Utils/pub-sub";

let initialState;

const KEY = {
  ESC: 27,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40
};

const DO_ANIM = true;
const NO_ANIM = false;

const MENU_OPEN = true;
const MENU_CLOSE = false;

const DO_FOCUS_BACK = true;
const NO_FOCUS_BACK = false;

const LEVEL_1 = 0;
const LEVEL_2 = 1;
const LEVEL_3 = 2;

const ACTIVE_CLASS = "is-active";
const SCROLL_STICKY_BASE = 10;
const MARKER_INITIAL_POS = 16;
const MARKER_STICKY_POS = 82;

var state = {
  active: null,
  activeLevel: LEVEL_1,
  isMegaMenuOpen: false,
  openMegaMenu: null,
  expandedButtons: []
};

const dom = {
  headerRoot: ".js-header",
  menuRoot: ".js-menu",
  mainContent: ".dcw_main",
  mainNav: ".dcw_main-nav_list",
  firstLevel: ".js-menu_item",
  secondLevel: ".js-mega-menu_item",
  secondLevelShown: ".js-mega-menu_item:not(.hide-on-desktop)",
  thirdLevel: ".js-mega-menu_subitem",
  thirdLevelShown: ".js-mega-menu_subitem:not(.hide-on-desktop)",
  marker: ".js-marker",
  megaMenuContainer: ".js-mega-menu",
  menuLink: ".dcw_mega-menu_link",
  activeClass: ".is-active",
  closeBtn: ".js-mega-menu_btn-close"
};

const eventHandlers = {
  preventMenuPropagation: _preventMenuClickPropagation,
  firstLevel: _handleFirstLevel
};

let domCache;
let firstLevelMarker;
var thirdLevelMarker;
let sticky;
let mainSearch;

export function initMenu() {
  domCache = getDomCache();
  setInitialState();
  bindEvents();
  sticky = setSticky();
  initMarkers();
  padContent();
  mainSearch = createMainSearch();

  return {
    destroy,
    authState: "NO_AUTH"
  };
}

function destroy() {
  unbindAllEvents();
  sticky.destroy();
  mainSearch.destroy();
}

function createMainSearch() {
  return new MainSearchUnAuth({
    $el: document.querySelector(".js-main-search"),
    itemIdPrefix: "main-search"
  });
}

function unbindAllEvents() {
  unbindCloseEvents();
  removeListeners(domCache.root);
}

function getDomCache() {
  return {
    root: document.querySelector(".dcw_main-nav"),
    menuRoot: document.querySelector(dom.menuRoot),
    headerRoot: document.querySelector(dom.headerRoot),
    firstMarker: document.querySelector(dom.marker)
  };
}

function setInitialState() {
  state.active = getActiveItems();
  initialState = deepClone(state);
}

function resetInitialState() {
  state = deepClone(initialState);
}

function setActiveLevel(level) {
  state.activeLevel = level;
}

function resetSubLevelsState() {
  state.active.slice(1).forEach(element => {
    let btn = element.querySelector("button");
    element.classList.remove(ACTIVE_CLASS);
  });
  unsetAllAriaExpanded();
}

function applyLevelsState(menuItem) {
  let secondLevel;
  let thirdLevel;
  let shouldTranslateMarker = false;
  let menuItemBtn = menuItem.querySelector("button");

  if (isCurrent(menuItem)) {
    [secondLevel, thirdLevel] = initialState.active.slice(1);
    shouldTranslateMarker = true;
  } else {
    secondLevel = menuItem.querySelector(dom.secondLevelShown);
    thirdLevel = secondLevel && secondLevel.querySelector(dom.thirdLevelShown);
  }

  requestAnimationFrame(() => {
    menuItem.classList.add(ACTIVE_CLASS);
    setAriaExpanded(menuItemBtn);

    if (secondLevel) {
      secondLevel.classList.add(ACTIVE_CLASS);
      setAriaExpanded(secondLevel.querySelector("button"));

      if (thirdLevel) {
        thirdLevel.classList.add(ACTIVE_CLASS);
        thirdLevelMarker = getSecondMarker(secondLevel);

        if (shouldTranslateMarker) {
          setTimeout(
            () => thirdLevelMarker.translateTo(thirdLevel, NO_ANIM),
            30
          );
        }
      }
    }

    updateSubLevelState(secondLevel, thirdLevel);
  });
}

function unsetLastActive(level) {
  return (
    state.active &&
    state.active[level] &&
    state.active[level].classList.remove(ACTIVE_CLASS)
  );
}

function setActive(level, element) {
  if (!state.active) {
    state.active = [];
  }

  if (state.active.length - 1 < level) {
    return state.active.push(element);
  }

  state.active[level] = element;
}

function updateSubLevelState(second, third) {
  if (second) setActive(LEVEL_2, second);
  if (third) setActive(LEVEL_3, third);
}

// marker

function initMarkers() {
  firstLevelMarker = getFirstMarker();

  if (initialState.active) {
    firstLevelMarker.translateTo(initialState.active[LEVEL_1]);
  }

  onElementBoundsOut(
    document.querySelector(".dcw_main-nav_list"),
    domCache.headerRoot,
    () => {
      setMarkerToLastActive();
    }
  );
}

function getFirstMarker() {
  return marker(
    "HORIZONTAL",
    domCache.firstMarker,
    dom.firstLevel,
    MARKER_INITIAL_POS
  );
}

function getSecondMarker(menuItem) {
  return marker(
    "VERTICAL",
    menuItem.querySelector(dom.marker),
    menuItem.querySelectorAll(dom.thirdLevel)
  );
}

function resetMarkerBackToInitial() {
  return (
    initialState.active &&
    firstLevelMarker.translateTo(initialState.active[LEVEL_1])
  );
}

function setMarkerToLastActive() {
  if (state.active) {
    return firstLevelMarker.translateTo(state.active[LEVEL_1]);
  }
  firstLevelMarker.translateAndHide(MARKER_INITIAL_POS);
}

function resetSecondMarker() {
  if (!thirdLevelMarker) return;

  thirdLevelMarker.translate(-24);
  thirdLevelMarker = null;
}

// sticky

function setSticky() {
  return stickyModule({
    $el: domCache.headerRoot,
    stickyLimit: SCROLL_STICKY_BASE,

    enterSticky: () => {
      firstLevelMarker.changePadTo(MARKER_STICKY_POS);
      return firstLevelMarker.replayTranslate(true);  
    },
    leaveSticky: () => {
      firstLevelMarker.changePadTo(MARKER_INITIAL_POS);
      return firstLevelMarker.replayTranslate(true);
    }
  });
}

// handling content below menu

function padContent() {
  document.querySelector(dom.mainContent).style.marginTop =
    domCache.headerRoot.clientHeight + "px";
}
// events

function bindEvents() {
  preventMenuPropagation();
  bindFirstLevel();
  bindSecondLevel();
  bindKeys();
}

function preventMenuPropagation() {
  domCache.menuRoot.addEventListener(
    "click",
    eventHandlers.preventMenuPropagation
  );
}

function _preventMenuClickPropagation(e) {
  e.stopPropagation();

  if (isCloseBtn(e)) {
    closeMegaMenu(DO_ANIM);
  }
}

function isCloseBtn(e) {
  let target = e.target;
  let closeBtnClass = dom.closeBtn.slice(1);

  return (
    target.classList.contains(closeBtnClass) ||
    target.parentNode.classList.contains(closeBtnClass)
  );
}

function removeListeners(el) {
  var newEl = el.cloneNode(true);
  el.parentNode.replaceChild(newEl, el);
}

function bindFirstLevel() {
  document.querySelector(dom.mainNav).addEventListener("click", e => {
    let element = e.target;
    let menuItem;

    if (element.classList.contains(dom.firstLevel.slice(1))) {
      menuItem = element;
    } else if (element.classList.contains("dcw_main-nav_link")) {
      menuItem = element.parentNode;
    }

    if (!menuItem) return;

    onEnterFirstLevel(menuItem);
  });

  document.querySelector(dom.mainNav).addEventListener("focusin", e => {
    let element = e.target;

    if (element.classList.contains("dcw_main-nav_link")) {
      firstLevelMarker.translateTo(element);
    }
  });
}

function _handleFirstLevel() {}

function bindSecondLevel() {
  [].forEach.call(
    document.querySelectorAll(".dcw_mega-menu_list"),
    secondMenu => {
      secondMenu.addEventListener("click", e => {
        let element = e.target;
        let menuItem;

        if (element.classList.contains("dcw_mega-menu_item")) {
          menuItem = element;
        } else if (element.classList.contains("dcw_mega-menu_link")) {
          menuItem = element.parentNode;
        }

        if (!menuItem || isThirdLevelItem(menuItem)) return;

        onEnterSecondLevel(menuItem);
      });
    }
  );
}

function isThirdLevelItem(item){
  return item.classList.contains("dcw_mega-menu_subitem") || typeof item.getAttribute("href") === "string";
}

function onEnterFirstLevel(menuItem) {
  resetSecondMarker();

  switch (state.isMegaMenuOpen) {
    case MENU_OPEN:
      handleMenuOpen(menuItem);
      break;
    case MENU_CLOSE:
      handleMenuClose(menuItem);
      break;
  }
}

function onEnterSecondLevel(menuItem) {
  let menuItemBtn;

  if (hasActiveClass(menuItem)) return;
  resetSecondMarker();

  unsetLastActive(LEVEL_2);
  unsetLastAriaExpanded();

  setActive(LEVEL_2, menuItem);
  state.active[LEVEL_3] = state.active[LEVEL_2].querySelector(dom.thirdLevel);
  thirdLevelMarker = getSecondMarker(menuItem);
  menuItemBtn = menuItem.querySelector("button");
  focusThirdLevel();
  setActiveLevel(LEVEL_3);
  menuItem.classList.add(ACTIVE_CLASS);
  setAriaExpanded(menuItemBtn);
}

function handleMenuOpen(menuItem) {
  if (isActive(LEVEL_1, menuItem)) {
    return;
  }

  closeMegaMenu(NO_ANIM, NO_FOCUS_BACK);
  applyLevelsState(menuItem);
  unsetLastActive(LEVEL_1);
  setActive(LEVEL_1, menuItem);
  openMegaMenu(menuItem, NO_ANIM);
  bindCloseEvents();
}

function handleMenuClose(menuItem) {
  PubSub.publish("search:close");

  unsetLastActive(LEVEL_1);
  setActive(LEVEL_1, menuItem);
  applyLevelsState(menuItem);
  openMegaMenu(menuItem, DO_ANIM);
  bindCloseEvents();
}

function bindCloseEvents() {
  window.addEventListener("click", handleCloseEvents);
  window.addEventListener("scroll", handleCloseEvents);
}

function unbindCloseEvents() {
  window.removeEventListener("click", handleCloseEvents);
  window.removeEventListener("scroll", handleCloseEvents);
}

function handleCloseEvents() {
  closeMegaMenu(DO_ANIM, DO_FOCUS_BACK);
}

function focusBackToLastActive(state) {}

// actions

function openMegaMenu(menuItem, shouldAnimate) {
  let megaMenu = getMegaMenuFrom(menuItem);
  let megaMenuItem = megaMenu.querySelector(dom.secondLevel);

  state.isMegaMenuOpen = true;
  state.openMegaMenu = megaMenu;

  if (shouldAnimate) {
    requestAnimationFrame(() => {
      animOpenMenu(megaMenu);
    });
  } else {
    megaMenu.classList.add("is-open", "is-open-add");
    showCloseBtn(megaMenu);
  }
}

function closeMegaMenu(shouldAnimate, shouldFocusBack) {
  unbindCloseEvents();

  if (shouldAnimate) {
    requestAnimationFrame(() => {
      animCloseMenu(state.openMegaMenu);
      resetSubLevelsState();
      resetInitialState();
    });
  } else {
    state.openMegaMenu.classList.remove("is-open", "is-open-add");

    unsetAllAriaExpanded();
    resetSubLevelsState();
    resetInitialState();
  }

  if (shouldFocusBack) {
    focusBackToLastActive();
    resetMarkerBackToInitial();
  }
}

function showCloseBtn(megaMenuRoot) {
  megaMenuRoot.querySelector(dom.closeBtn).style.display = "inline-block";
}

function hideCloseBtn(megaMenuRoot) {
  megaMenuRoot.querySelector(dom.closeBtn).style.display = "";
}

function setAriaExpanded(element) {
  element.setAttribute("aria-expanded", true);
  state.expandedButtons.push(element);
}

function unsetLastAriaExpanded() {
  let element = state.expandedButtons.pop();
  return element && unsetAriaExpanded(element);
}

function unsetAriaExpanded(element) {
  element.setAttribute("aria-expanded", false);
}

function unsetAllAriaExpanded() {
  state.expandedButtons.forEach(btn => {
    unsetAriaExpanded(btn);
  });
  state.expandedButtons = [];
}

// getters

function getActiveItems() {
  let firstActive = getFirstActive();
  let subLevels;

  if (!firstActive) return null;

  return [firstActive, ...getActiveSubLevelsFrom(firstActive)];
}

function getFirstActive() {
  return document.querySelector(dom.firstLevel + dom.activeClass);
}

function getParentElementOf(element) {
  return element.parentNode;
}

function getActiveFromInitialState(level) {
  return initialState.active[level] || null;
}

function getActiveSubLevelsFrom(element) {
  let entry = element.querySelector(dom.activeClass);

  if (entry) {
    return [entry].concat(getActiveSubLevelsFrom(entry));
  }

  return [];
}

function getMegaMenuFrom(menuItem) {
  return menuItem.querySelector(dom.megaMenuContainer);
}

// booleans

function hasActiveClass(element) {
  return element.classList.contains(ACTIVE_CLASS);
}

function isActive(level, menuItem) {
  return state.active[level] === menuItem;
}

function isCurrent(menuItem) {
  return initialState.active && initialState.active[LEVEL_1] === menuItem;
}

// anim
function animOpenMenu(menuItem) {
  menuItem.classList.add("is-open-add");

  requestAnimationFrame(() => {
    menuItem.classList.add("is-open");
  });
  setTimeout(() => showCloseBtn(menuItem), 400);
}

function animCloseMenu(menuItem) {
  menuItem.classList.remove("is-open");
  hideCloseBtn(menuItem);

  setTimeout(() => {
    menuItem.classList.remove("is-open-add");
  }, 300);
}

// KEY nav

function bindKeys() {
  domCache.menuRoot.addEventListener("keydown", handleKeyDown);
}

function handleKeyDown(e) {
  switch (state.activeLevel) {
    case LEVEL_1:
      handleLevelOneKey(e);
      break;
    case LEVEL_2:
      handleLevelTwoKey(e);
      break;
    case LEVEL_3:
      handleLevelThreeKey(e);
      break;
  }
}

function handleLevelOneKey(e) {
  switch (e.keyCode) {
    case KEY.ESC:
      closeMegaMenu(true);
      break;
    case KEY.DOWN:
      e.preventDefault();

      if (state.isMegaMenuOpen) {
        focusThirdLevel();
      }

      onEnterFirstLevel(getParentElementOf(e.target));
      break;
  }
}

function handleLevelTwoKey(e) {
  switch (e.keyCode) {
    case KEY.ESC:
      closeMegaMenu(true);
      break;
    case KEY.UP:
      e.preventDefault();
      focusPreviousItem();
      break;
    case KEY.DOWN:
      e.preventDefault();
      focusNextItem();
      break;
    case KEY.LEFT:
      setActiveLevel(LEVEL_2);
      break;
    case KEY.RIGHT:
      onEnterSecondLevel(getParentElementOf(e.target));
      break;
  }
}

function handleLevelThreeKey(e) {
  switch (e.keyCode) {
    case KEY.ESC:
      closeMegaMenu(true);
      break;
    case KEY.UP:
      e.preventDefault();
      focusPreviousItem();
      break;
    case KEY.DOWN:
      e.preventDefault();
      focusNextItem();
      break;
    case KEY.LEFT:
      setActiveLevel(LEVEL_2);
      getFocusableElementOf(state.active[LEVEL_2]).focus();
      break;
  }
}

function focusPreviousItem() {
  let parent = getParentElementOf(document.activeElement);
  let sibling = getPrevSiblingOfType(parent, "LI");
  let newFocus;

  if (!sibling) return;

  if ((newFocus = getFocusableElementOf(sibling))) {
    newFocus.focus();
  }
}

function focusNextItem() {
  let parent = getParentElementOf(document.activeElement);
  let sibling = getNextSiblingOfType(parent, "LI");
  let newFocus;

  if (!sibling) return;

  if ((newFocus = getFocusableElementOf(sibling))) {
    newFocus.focus();
  }
}

function focusThirdLevel() {
  setTimeout(() => getFocusableElementOf(state.active[LEVEL_3]).focus());
  setActiveLevel(LEVEL_3);
}

function getFocusableElementOf(element) {
  return element.querySelector(dom.menuLink);
}

function getNextSiblingOfType(element, type) {
  var sibling = element.nextSibling;

  while (sibling.tagName !== type) {
    sibling = sibling.nextSibling;
    if (!sibling) return null;
  }

  return sibling;
}

function getPrevSiblingOfType(element, type) {
  var sibling = element.previousSibling;

  while (sibling.tagName !== type) {
    sibling = sibling.previousSibling;
    if (!sibling) return null;
  }

  return sibling;
}



// WEBPACK FOOTER //
// ./src/components/header/header2.js